import { Optional } from '@vimeo/core/types/generic';

import { ELiveErrorCode } from '../../../../types/error';
import { TFirebaseReference, TFirebaseSnapshot } from '../../../../types/firebase';
import { IInteractionApplicationUser } from '../../../../types/interaction_room';
import { EChatType } from '../../../../types/interaction_tools';
import { LiveError } from '../../../../utils/error';
import {
  getRefChild,
  pushRefValue,
  removeRefValue,
  setRefValue,
} from '../../../../utils/firebase/cruds';
import {
  createMessageAuthorInteractionObject,
  createMessageInteractionObject,
} from '../../../../utils/firebase/interaction_objects';
import { getAbsoluteNow } from '../../../../utils/general/time';
import { queryInteractionUserSync } from '../../../../utils/store/interaction';
import { ChatManager } from './ChatManager';

/**
 * Enable chat widget.
 * Does not affect guest chat.
 */
export async function setChatEnabled(manager: ChatManager, isEnabled: boolean): Promise<void> {
  manager.log.info('Setting chat enabled:', isEnabled);
  manager.assertIsInitialized();

  await setRefValue(manager.firebase, manager.metaChatEnabledRef, isEnabled);
}

/**
 * Remove specified message by ID.
 *
 * Users can remove own messages only.
 * Owners can remove any message.
 */
export async function deleteChatMessage(
  manager: ChatManager,
  chatType: EChatType,
  messageId: string,
): Promise<void> {
  if (!messageId) {
    throw new LiveError('No message id supplied for removal.', {
      code: ELiveErrorCode.INVALID_PARAMETERS,
    });
  }

  manager.assertIsInitialized();

  /**
   * Get chat ref by active chat type.
   */
  const chatRef: TFirebaseReference = manager.getChatRef(chatType);

  manager.log.info('Deleting message', messageId);
  await removeRefValue(manager.firebase, getRefChild(manager.firebase, chatRef, messageId));
}

/**
 * Send chat message and include message sender as nested parameter.
 */
export async function sendChatMessage(
  manager: ChatManager,
  chatType: EChatType,
  message: string,
): Promise<void> {
  manager.log.info('Sending chat message');
  manager.assertIsInitialized();

  const interactionUser: Optional<IInteractionApplicationUser> = queryInteractionUserSync(manager);

  if (!interactionUser) {
    throw new LiveError('User should be authorized to send chat messages.', {
      code: ELiveErrorCode.UNAUTHORIZED,
    });
  }

  /**
   * Get chat ref by active chat type.
   */
  const chatRef: TFirebaseReference = manager.getChatRef(chatType);

  /**
   * Create message object
   */
  const messageObject = createMessageInteractionObject({
    contents: message,
    createdAt: getAbsoluteNow(),
    user: createMessageAuthorInteractionObject({
      id: String(interactionUser.id),
      displayName: interactionUser.displayName,
      avatarUrl: interactionUser.avatarUrl,
      isCreator: Boolean(interactionUser.isCreator),
    }),
  });

  /**
   * Publish message in strictly defined format.
   */
  await pushRefValue(manager.firebase, chatRef, {
    ...messageObject,
    createdAt: manager.firebase.database.serverTimestamp(),
  });

  manager.log.info('Sent chat message:', message.length);
}

/**
 * Force user to ban list, banned users are shadow-banned and their messages are not visible for others.
 */
export async function blockChatUser(
  manager: ChatManager,
  chatType: EChatType,
  userId: string,
): Promise<void> {
  if (!userId) {
    throw new LiveError('No user id supplied to block user.', {
      code: ELiveErrorCode.INVALID_PARAMETERS,
    });
  }

  manager.assertIsInitialized();

  /**
   * Get ban list ref by active chat type.
   */
  const banListRef: Optional<TFirebaseReference> = manager.getChatBanListRef(chatType);

  if (banListRef) {
    manager.log.info('Blocking chat user:', userId);

    await pushRefValue(manager.firebase, banListRef, { userId });
  }
}

/**
 * Unblock user from chat.
 * Removes user from ban list.
 */
export async function unblockChatUser(
  manager: ChatManager,
  chatType: EChatType,
  userId: string,
): Promise<void> {
  if (!userId) {
    throw new LiveError('No user id supplied to unblock user.', {
      code: ELiveErrorCode.INVALID_PARAMETERS,
    });
  }

  manager.assertIsInitialized();

  /**
   * Get ban list ref by active chat type.
   */
  const banListRef: Optional<TFirebaseReference> = manager.getChatBanListRef(chatType);

  if (banListRef) {
    manager.log.info('Unblocking chat user:', userId);

    const snapshot: TFirebaseSnapshot = await manager.firebase.database.get(
      manager.firebase.database.query(
        banListRef,
        manager.firebase.database.orderByChild('userId'),
        manager.firebase.database.equalTo(userId),
      ),
    );

    snapshot.forEach((child: TFirebaseSnapshot): boolean =>
      Boolean(
        removeRefValue(
          manager.firebase,
          getRefChild(manager.firebase, banListRef, child.key as string),
        ),
      ),
    );
  }
}

/**
 * Dispose chat manager entry and clean up data.
 */
export function dispose(manager: ChatManager): void {
  manager.log.info('Disposing manager');
  manager.disposeSubscribers();

  manager.publicChatBanListRef = null;
  manager.metaChatEnabledRef = null;
  manager.publicChatRef = null;
  manager.guestsChatRef = null;

  manager.setContext({
    [EChatType.PUBLIC]: ChatManager.createNewChatDescriptor(),
    [EChatType.BACKSTAGE]: ChatManager.createNewChatDescriptor(),
    isEnabled: null,
    isHydrated: false,
    config: {
      roomId: -1,
      isCreator: false,
    },
    chatSessionsHistory: [],
  });
}
